/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import BasicDialog from "../../../components/Shared/Dialogs/BasicDialog";
import { useSelector } from "react-redux";
import { failedBulkSelector, successBulkSelector } from "../../../store/api/admin/selectors";

const BulkOfflineStatusModal = () => {
  const dispatch = useStoreDispatch();
  const FailedBulkUploads = useSelector(failedBulkSelector);
  const SuccessBulkUploads = useSelector(successBulkSelector);
  const [activeTab, setActiveTab] = useState("failed");

  const FailedStatus = () => {
    return (
      <div className="flex flex-col gap-4">
        {FailedBulkUploads?.length > 0 ? (
          FailedBulkUploads?.map((item: any, index: number) => (
            <div
              className="p-2 border border-border text-foreground bg-muted rounded"
              key={index}
            >
              {item}
            </div>
          ))
        ) : (
          <div>
            <p>{`${FailedBulkUploads?.length || 0} errors occured`}</p>
          </div>
        )}
      </div>
    );
  };

  const SuccessStatus = () => {
    return (
      <div className="flex flex-col gap-4">
        {SuccessBulkUploads?.length > 0 ? (
          SuccessBulkUploads?.map((item: any, index: number) => (
            <div
              className="p-2 border border-border text-foreground bg-muted rounded"
              key={index}
            >
              {`${item?.name} is added successfully.`}
            </div>
          ))
        ) : (
          <div>
            <p>{`${SuccessBulkUploads?.length || 0} rows validated`}</p>
          </div>
        )}
      </div>
    );
  };

  const tabData = [
    {
      label: "Failed",
      value: "failed",
      count: FailedBulkUploads?.length || 0,
      countVariant: "destructive" as const,
      component: <FailedStatus />,
    },
    {
      label: "Successful",
      value: "successful",
      count: SuccessBulkUploads?.length || 0,
      countVariant: "default" as const,
      component: <SuccessStatus />,
    },
  ];

  const dialogAction = (
    <div className="flex justify-end items-center pt-2 pb-2">
      <Button variant="outline" onClick={() => dispatch(setBasicDialogOpenFor(undefined))}>
        Cancel
      </Button>
    </div>
  );

  return (
    <BasicDialog size="md" headerTitle="Add-Offline devices status" dialogActions={dialogAction} headerTheme={true}>
      <div className="h-[400px]">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div style={{ borderBottom: "1px solid var(--color-border)" }}>
            <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
              {tabData.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none gap-2"
                >
                  {tab.label}
                  <Badge variant={tab.countVariant} className="text-xs px-1.5 py-0">
                    {tab.count}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {tabData.map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="pt-4">
              {tab.component}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </BasicDialog>
  );
};

export default BulkOfflineStatusModal;
